/**
 * Deep links into Sentry's issue stream for one user.
 *
 * The apps tag every event with the signed-in account (see `toSentryUser` in
 * @produce/shared), which is what makes "has this person hit an error?"
 * answerable at all. This builds the search URL the admin panel links to, so
 * the query syntax lives in one place rather than in a template string in a
 * component.
 */

const DEFAULT_ORG = "descone";

/**
 * Sentry organisation slug.
 *
 * Read from `VITE_SENTRY_ORG` the way `instrument.ts` reads its DSN, so a
 * deployment under a different Sentry org links to its own issue stream. The
 * argument exists so the resolution rule can be tested without stubbing
 * `import.meta.env`.
 */
export function sentryOrg(configured?: string): string {
  const org = (configured ?? import.meta.env.VITE_SENTRY_ORG ?? "").trim();
  return org.length > 0 ? org : DEFAULT_ORG;
}

/**
 * Issue-stream URL filtered to one user's email, over the last 90 days.
 * Returns null without an email — a link that searches for nothing would look
 * like "no errors" rather than "we cannot tell".
 */
export function sentryUserIssuesUrl(
  email: string | null | undefined,
  configuredOrg?: string,
): string | null {
  if (!email) return null;
  const query = encodeURIComponent(`user.email:"${email}"`);
  return `https://${sentryOrg(configuredOrg)}.sentry.io/issues/?query=${query}&statsPeriod=90d`;
}
